import React from "react";
import { ClockIcon } from "@heroicons/react/20/solid";
import type { DateRangeFilter } from "../types";
import type { DateWindow } from "../utils/dateWindow";
import { windowLabel } from "../utils/dateWindow";

interface WindowStaleNoticeProps {
  /** The window in effect — only rolling windows can go stale. */
  dateWindow?: DateWindow;
  /** The range currently on screen, resolved before the date rolled over. */
  showing?: DateRangeFilter;
  onRefresh: () => void;
}

/**
 * Shown when the local date has moved past the day the rolling window was
 * resolved against — i.e. a tab left open across midnight.
 *
 * It deliberately does *not* re-resolve on its own. Both silent options fail:
 * leaving the window alone shows data ending yesterday, and shifting it
 * automatically moves the ground under someone reading a number off the chart.
 * So the rollover is reported and the move is the user's (design doc 033, A1).
 */
export function WindowStaleNotice({
  dateWindow,
  showing,
  onRefresh,
}: WindowStaleNoticeProps) {
  if (!showing?.start_date && !showing?.end_date) return null;

  const shown = windowLabel({
    kind: "absolute",
    start: showing?.start_date,
    end: showing?.end_date,
  });

  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground border border-border bg-card rounded-lg px-4 py-2"
    >
      <ClockIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>
        Showing <span className="text-foreground">{shown}</span> · the date has
        changed
      </span>
      <button
        type="button"
        onClick={onRefresh}
        className="font-medium text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-ring rounded"
      >
        Update to {windowLabel(dateWindow).toLowerCase()}
      </button>
    </div>
  );
}
